import React, { useEffect, useState, useCallback } from "react";
import { debounce } from "lodash";
import { useTranslation } from "react-i18next";

import {
  searchHashTag,
  getHashTagById,
  getRecentHashtags,
} from "services/pictureServices";
import type { ActivityTag } from "types/ActivityTag";

interface HashtagInputProp {
  inputValue: string;
  onInputChange: (value: string) => void;
  value: number | null;
  onChange: (value: number | null) => void;
}

const HashtagInput: React.FC<HashtagInputProp> = ({
  inputValue,

  onInputChange,
  value,
  onChange,
}) => {
  const { t } = useTranslation();
  const [suggestion, setSuggestion] = useState<ActivityTag[]>([]);
  const [recentHashtags, setRecentHashtags] = useState<ActivityTag[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Fetch recent hashtags on component mount
  useEffect(() => {
    const fetchRecentHashtags = async () => {
      setIsLoading(true);
      try {
        const recent = await getRecentHashtags();
        setRecentHashtags(recent);
      } catch (e) {
        console.error("Error fetching recent hashtags:", e);
      } finally {
        setIsLoading(false);
      }
    };

    fetchRecentHashtags();
  }, []);

  const getHashtag = async () => {
    try {
      const hashtag = await getHashTagById(value || 0);
      onInputChange(hashtag.name);
    } catch (e) {}
  };

  useEffect(() => {
    if (!value) return;
    getHashtag();
  }, [value]);

  const search = useCallback(
    debounce(async (query: string) => {
      try {
        const tags = await searchHashTag(query);
        setSuggestion(tags);
      } catch (e) {}
    }, 300),
    []
  );

  useEffect(() => {
    if (inputValue.length === 0 || value) {
      setSuggestion([]);
      return;
    }
    search(inputValue);
  }, [inputValue, search]);

  // Handle hashtag selection
  const handleHashtagSelect = (hashtag: ActivityTag) => {
    onChange(hashtag.id);
    setSuggestion([]);
    setIsFocused(false);
  };

  // Handle blur with delay to allow clicks to register
  const handleBlur = () => {
    // Delay hiding dropdown to allow click events to fire first
    setTimeout(() => {
      setIsFocused(false);
    }, 150);
  };

  // Determine what to show in the dropdown
  const showRecentHashtags =
    inputValue.length === 0 && !value && recentHashtags.length > 0 && isFocused;
  const showSuggestions = suggestion.length > 0 && isFocused;
  const showDropdown = showRecentHashtags || showSuggestions;

  return (
    <div className="w-full h-full relative">
      <div className="flex h-[46px] min-h-[46px] w-full flex-col justify-center rounded-md border border-[#9B9B9B] bg-white px-3 py-1.5">
        <p className="truncate text-[10px] leading-none text-[#333333]">
          {t("_published_picture._include_activity_tags")}
        </p>
        <input
          className="w-full min-w-0 overflow-hidden text-ellipsis bg-transparent text-sm leading-tight outline-none disabled:bg-white/50"
          name={"hashtag"}
          placeholder={"#"}
          autoComplete="off"
          value={`#${inputValue}`}
          onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
            const newValue = e.target.value.replace("#", "");
            if (newValue.includes(" ") || newValue.includes("#")) return;
            onInputChange(newValue);
            onChange(null);
          }}
          onFocus={() => setIsFocused(true)}
          onBlur={handleBlur}
        />
      </div>

      {showDropdown && (
        <ul className="w-full bg-gray-50 rounded-lg p-2 max-h-[400px] overflow-auto absolute z-40 list-none border-2 border-gray-300 shadow-lg">
          {/* Show recent hashtags when input is empty */}
          {showRecentHashtags && (
            <>
              {recentHashtags.map((hashtag) => (
                <li
                  key={hashtag.id}
                  className="p-2 cursor-pointer hover:bg-[#F4F4F4] flex justify-between items-center"
                  onMouseDown={(e) => {
                    // Prevent default to avoid input blur
                    e.preventDefault();
                    handleHashtagSelect(hashtag);
                  }}
                >
                  <span>#{hashtag.name}</span>
                </li>
              ))}
            </>
          )}

          {/* Show search suggestions when user is typing */}
          {showSuggestions && (
            <>
              {showRecentHashtags && (
                <li className="p-2 text-sm text-gray-600 font-medium border-b border-gray-200 mb-1 mt-2">
                  Search Results
                </li>
              )}
              {suggestion.map((s) => (
                <li
                  key={s.id}
                  className="p-2 cursor-pointer hover:bg-[#F4F4F4]"
                  onMouseDown={(e) => {
                    // Prevent default to avoid input blur
                    e.preventDefault();
                    handleHashtagSelect(s);
                  }}
                >
                  #{s.name}
                </li>
              ))}
            </>
          )}

          {/* Loading state */}
          {isLoading && (
            <li className="p-2 text-sm text-gray-500">
              Loading recent hashtags...
            </li>
          )}
        </ul>
      )}
    </div>
  );
};

export default HashtagInput;
